import { useQuery } from '@powersync/react';
import { useState } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedRef } from 'react-native-reanimated';

import { DangerRow } from '@/components/domain/DangerRow';
import { SyncStatusArea } from '@/components/domain/syncStatus/SyncStatusArea';
import { SettingsGroup } from '@/components/domain/SettingsGroup';
import { SettingsRow } from '@/components/domain/SettingsRow';
import { BoundedScrollView } from '@/components/ui/BoundedScrollView';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Form } from '@/components/ui/Form';
import { NavBar } from '@/components/ui/NavBar';
import { PasswordField } from '@/components/ui/PasswordField';
import { Screen } from '@/components/ui/Screen';
import { Text } from '@/components/ui/Text';
import { TextField } from '@/components/ui/TextField';
import { useAuth } from '@/lib/auth';
import { confirmDestructive } from '@/lib/confirm';
import { MIN_PASSWORD_LENGTH, USERNAME_MAX_LENGTH } from '@/lib/constants';
import { updateProfile } from '@/lib/data';
import { emailLocalPart } from '@/lib/format';
import { useGoBack } from '@/lib/navigation';
import { supabase } from '@/lib/supabase';
import { useAsyncAction } from '@/lib/useAsyncAction';
import { useInFlightGuard } from '@/lib/useInFlightGuard';

type EditField = 'name' | 'email' | 'password' | null;

export default function AccountScreen() {
  const goBack = useGoBack('/settings');
  const syncScrollRef = useAnimatedRef<Animated.ScrollView>();
  const { session, signOut } = useAuth();
  const { data: profiles } = useQuery<{ display_name: string | null }>(
    'SELECT display_name FROM profiles WHERE id = ?',
    [session?.user.id ?? ''],
  );
  const email = session?.user.email ?? '';
  const displayName = profiles[0]?.display_name ?? emailLocalPart(email) ?? 'You';

  const [editing, setEditing] = useState<EditField>(null);
  const [value, setValue] = useState('');
  // The current password, required to re-authenticate before a password change.
  const [currentPassword, setCurrentPassword] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const { busy, error, setError, run: runSave } = useAsyncAction();
  const runDelete = useInFlightGuard();

  function open(field: EditField, initial: string) {
    setEditing(field);
    setValue(initial);
    setCurrentPassword('');
    setError(null);
    setStatus(null);
  }

  async function save() {
    await runSave(async () => {
      if (editing === 'name') {
        await updateProfile({ display_name: value.trim() || null });
      } else if (editing === 'email') {
        const { error: e } = await supabase.auth.updateUser({ email: value.trim() });
        if (e) throw e;
        setStatus('Check your inbox to confirm the new email.');
      } else if (editing === 'password') {
        if (value.length < MIN_PASSWORD_LENGTH) {
          throw new Error(
            `Use ${MIN_PASSWORD_LENGTH} or more characters for your password.`,
          );
        }
        // Re-authenticate with the current password before changing it, so a
        // briefly-unattended session can't silently take over the account.
        // (secure_password_change alone only prompts for stale sessions and
        // never checks the current password.) Signing in also refreshes the
        // session, satisfying GoTrue's reauthentication requirement.
        const { error: reauthErr } = await supabase.auth.signInWithPassword({
          email,
          password: currentPassword,
        });
        if (reauthErr) throw new Error('Current password is incorrect.');
        const { error: e } = await supabase.auth.updateUser({ password: value });
        if (e) throw e;
        setStatus('Password updated.');
      }
      setEditing(null);
    });
  }

  async function confirmDelete() {
    await runDelete(async () => {
      const ok = await confirmDestructive(
        'Delete account?',
        'This permanently removes your templates and run logs. This cannot be undone.',
      );
      if (!ok) return;
      try {
        const { error: e } = await supabase.functions.invoke('delete-account');
        if (e) throw e;
        await signOut();
      } catch (e) {
        setError((e as Error).message);
      }
    });
  }

  return (
    <Screen edges={['top']}>
      <NavBar onBack={goBack} title="Account" />

      <SyncStatusArea scrollRef={syncScrollRef}>
        <BoundedScrollView
          scrollRef={syncScrollRef}
          alwaysBounceVertical
          contentContainerClassName="gap-6 px-5 pb-10 pt-2"
        >
          <SettingsGroup title="Profile">
            <SettingsRow
              label="Username"
              value={displayName}
              onPress={() => open('name', displayName)}
            />
            <SettingsRow
              label="Email"
              value={email}
              onPress={() => open('email', email)}
            />
            <SettingsRow
              label="Password"
              value="••••••••"
              onPress={() => open('password', '')}
            />
          </SettingsGroup>

          {status ? <Text variant="caption">{status}</Text> : null}
          {error ? (
            <Text variant="caption" className="text-alert">
              {error}
            </Text>
          ) : null}

          <SettingsGroup title="Danger zone">
            <DangerRow label="Sign out" onPress={signOut} />
            <DangerRow label="Delete account" onPress={confirmDelete} />
          </SettingsGroup>
        </BoundedScrollView>
      </SyncStatusArea>

      <Dialog visible={editing !== null} onClose={() => setEditing(null)}>
        <Form onSubmit={save}>
          <Text variant="heading" className="mb-3">
            {editing === 'name'
              ? 'Edit username'
              : editing === 'email'
                ? 'Change email'
                : 'Change password'}
          </Text>
          {editing === 'password' ? (
            <View className="gap-3">
              <PasswordField
                value={currentPassword}
                onChangeText={setCurrentPassword}
                placeholder="Current password"
                autoComplete="current-password"
                textContentType="password"
              />
              <PasswordField
                value={value}
                onChangeText={setValue}
                placeholder="New password"
                autoComplete="new-password"
                textContentType="newPassword"
                showStrength
              />
            </View>
          ) : (
            <TextField
              value={value}
              onChangeText={setValue}
              autoCapitalize="none"
              autoComplete={editing === 'email' ? 'email' : 'off'}
              textContentType={editing === 'email' ? 'username' : undefined}
              importantForAutofill={editing === 'email' ? 'yes' : 'no'}
              keyboardType={editing === 'email' ? 'email-address' : 'default'}
              maxLength={editing === 'name' ? USERNAME_MAX_LENGTH : undefined}
              autoFocus
            />
          )}
          <View className="mt-4 flex-row gap-2">
            <Button
              className="flex-1"
              variant="outline"
              label="Cancel"
              onPress={() => setEditing(null)}
            />
            <Button className="flex-1" submit label="Save" busy={busy} />
          </View>
        </Form>
      </Dialog>
    </Screen>
  );
}
